import React, { useState, useEffect } from "react";
import axios from "axios";
import { Package, Search, Tag, Box, Loader2 } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { useTheme } from "../../contexts/ThemeContext";

const API = process.env.REACT_APP_BACKEND_URL;

const TechStock = () => {
  const { isDark } = useTheme();
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const fetchStock = async () => {
      try {
        const token = localStorage.getItem("tech_token");
        const res = await axios.get(`${API}/api/tech/my-stock`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStock(res.data);
      } catch {
        toast.error("Failed to load your stock");
      } finally {
        setLoading(false);
      }
    };
    fetchStock();
  }, []);

  const filtered = stock.filter((s) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      s.name?.toLowerCase().includes(q) ||
      s.sku?.toLowerCase().includes(q) ||
      s.category?.toLowerCase().includes(q)
    );
  });

  const categories = [...new Set(stock.map((s) => s.category).filter(Boolean))];
  const totalItems = stock.reduce((sum, s) => sum + s.quantity, 0);

  const cardBg = isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-200 shadow-sm";
  const cardYellow = isDark ? "bg-[#FFCC00]/[0.06] border-[#FFCC00]/10" : "bg-amber-50 border-amber-200";
  const cardBlue = isDark ? "bg-blue-500/[0.06] border-blue-500/15" : "bg-blue-50 border-blue-200";

  return (
    <div className="space-y-5" data-testid="tech-stock-page">
      <div>
        <h1 className="text-xl font-bold text-foreground font-['Manrope']">My Stock</h1>
        <p className="text-muted-foreground text-sm mt-0.5">Parts and materials assigned to you</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className={`rounded-xl p-3 border ${cardYellow}`}>
          <Package className="w-4 h-4 text-[#FFCC00] mb-1" />
          <p className="text-foreground text-lg font-bold font-['Manrope']" data-testid="total-types">{stock.length}</p>
          <p className="text-muted-foreground text-xs">Item Types</p>
        </div>
        <div className={`rounded-xl p-3 border ${cardBlue}`}>
          <Box className="w-4 h-4 text-blue-500 mb-1" />
          <p className="text-foreground text-lg font-bold font-['Manrope']" data-testid="total-units">{totalItems}</p>
          <p className="text-muted-foreground text-xs">Total Units</p>
        </div>
        <div className={`rounded-xl p-3 col-span-2 sm:col-span-1 border ${cardBg}`}>
          <Tag className="w-4 h-4 text-green-500 mb-1" />
          <p className="text-foreground text-lg font-bold font-['Manrope']" data-testid="total-categories">{categories.length}</p>
          <p className="text-muted-foreground text-xs">Categories</p>
        </div>
      </div>

      {/* Search */}
      {stock.length > 0 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search parts..."
            className={`pl-10 h-9 ${isDark ? "bg-white/[0.04] border-white/10 text-white placeholder:text-white/30" : "bg-white border-gray-200 text-gray-900 placeholder:text-gray-400"}`}
            data-testid="stock-search"
          />
        </div>
      )}

      {/* Stock list */}
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : stock.length === 0 ? (
        <div className="text-center py-16" data-testid="empty-stock">
          <Package className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">No stock assigned to you yet</p>
          <p className="text-muted-foreground/60 text-xs mt-1">Your admin will assign parts when needed</p>
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-center text-muted-foreground text-sm py-8">No items match your search</p>
      ) : (
        <div className="space-y-2" data-testid="stock-list">
          {filtered.map((item) => (
            <div
              key={item.item_id}
              className={`flex items-center gap-3 rounded-xl p-3 transition-colors border ${cardBg}`}
              data-testid={`stock-item-${item.item_id}`}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? "bg-[#FFCC00]/[0.08]" : "bg-amber-50"}`}>
                <Package className="w-5 h-5 text-[#FFCC00]/70" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-foreground text-sm font-medium truncate">{item.name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  {item.sku && <span className="text-muted-foreground text-xs">{item.sku}</span>}
                  {item.category && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-border text-muted-foreground">
                      {item.category}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-foreground font-bold text-sm font-['Manrope']" data-testid={`qty-${item.item_id}`}>
                  {item.quantity}
                </p>
                <p className="text-muted-foreground text-xs">{item.unit}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TechStock;
